import { readRecovery, writeRecovery } from './recoveryStore'
import { roomFetch } from './roomApi'
import type { BibleImage, StoryProject } from './types'

export const imageUrl = (assetId: string) => '/api/images/' + encodeURIComponent(assetId)
export const projectImages = (project: StoryProject) => [...(project.bible?.characters || []), ...(project.bible?.entries || [])].flatMap(item => item.images || [])
const keyFor = (userId: string, id: string) => 'bramble:account:' + userId + ':image:' + id
const loading = new Map<string, Promise<Blob>>()
const cachePicture = async (key: string, blob: Blob) => writeRecovery(key, { bytes: await blob.arrayBuffer(), mimeType: blob.type })

export function pictureBlob(image: BibleImage, userId: string): Promise<Blob> {
  if (image.dataUrl) return fetch(image.dataUrl).then(response => response.blob())
  const key = keyFor(userId, image.assetId!)
  const existing = loading.get(key)
  if (existing) return existing
  const operation = (async () => {
    const cached = await readRecovery(key) as { bytes?: ArrayBuffer; mimeType?: string } | null
    if (cached?.bytes instanceof ArrayBuffer && cached.bytes.byteLength === image.size && cached.mimeType === image.mimeType)
      return new Blob([cached.bytes], { type: cached.mimeType })
    const response = await roomFetch(userId, imageUrl(image.assetId!))
    if (!response.ok) throw new Error('Reconnect to load “' + image.name + '”.')
    const blob = await response.blob()
    if (blob.size !== image.size || blob.type !== image.mimeType) throw new Error('This picture could not be read: ' + image.name)
    await cachePicture(key, blob)
    return blob
  })()
  loading.set(key, operation)
  void operation.finally(() => { if (loading.get(key) === operation) loading.delete(key) }).catch(() => undefined)
  return operation
}

export async function uploadPicture(image: BibleImage, userId: string): Promise<BibleImage> {
  // Offline additions stay embedded in the recovery draft until reconnection.
  if (!navigator.onLine) return image
  const blob = await pictureBlob(image, userId)
  let response: Response
  try { response = await roomFetch(userId, '/api/images', { method: 'POST', body: blob }) }
  catch { return image }
  const saved = await response.json()
  if (!response.ok) throw new Error(saved.error || 'This picture could not be uploaded.')
  await cachePicture(keyFor(userId, saved.assetId), blob)
  return { id: image.id, name: image.name, alt: image.alt, assetId: saved.assetId, mimeType: saved.mimeType, size: saved.size }
}

export async function portablePictures(project: StoryProject, userId: string): Promise<StoryProject> {
  const convert = async <T extends { images?: BibleImage[] }>(item: T): Promise<T> => ({ ...item,
    ...(item.images ? { images: await Promise.all(item.images.map(async image => {
      if (image.dataUrl) return image
      const blob = await pictureBlob(image, userId)
      const dataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => resolve(String(reader.result)); reader.onerror = () => reject(new Error('The picture could not be included in this backup.'))
        reader.readAsDataURL(blob)
      })
      return { id: image.id, name: image.name, alt: image.alt, dataUrl }
    })) } : {}),
  })
  if (!project.bible) return project
  return { ...project, bible: { ...project.bible,
    characters: await Promise.all(project.bible.characters.map(convert)),
    ...(project.bible.entries ? { entries: await Promise.all(project.bible.entries.map(convert)) } : {}),
  } }
}
